import React, { useState, useEffect } from 'react';
import { productService, API_ROOT_URL } from '../services/api';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  CircularProgress, 
  TextField, 
  InputAdornment,
  Chip,
  Divider,
  Paper,
  Grid,
  Card,
  CardContent,
  Container,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  AttachMoney as PriceIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Función para generar el nombre del producto
const generarNombreProducto = (producto) => {
  if (!producto || !producto.mercado || !producto.metal || !producto.presentacion) return 'Producto sin título';
  let extra = '';
  if (producto.especificaciones?.espesor) extra = ` - ${producto.especificaciones.espesor}mm`;
  if (producto.especificaciones?.diametro) extra = ` - Ø${producto.especificaciones.diametro}mm`;
  return `${producto.mercado} - ${producto.metal} - ${producto.presentacion}${extra}`;
};

// Función para obtener especificaciones clave
const especificacionesClave = (especificaciones) => {
  if (!especificaciones || typeof especificaciones !== 'object') return [];
  const claves = ['largo', 'ancho', 'espesor', 'norma', 'aleacion', 'acabado'];
  return claves.filter(k => especificaciones[k]).map(k => `${k}: ${especificaciones[k]}`);
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await productService.getProducts({ ordering: '-created_at' });
        const productsData = res.data.results ? res.data.results : res.data;
        setProducts(productsData);
        setFilteredProducts(productsData);
      } catch (err) {
        console.error('Error fetching products:', err);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Función de búsqueda
  useEffect(() => {
    const filtered = products.filter(product => {
      const searchLower = searchTerm.toLowerCase();
      const name = generarNombreProducto(product).toLowerCase();
      const description = (product?.description || '').toLowerCase();
      const metal = (product?.metal || '').toLowerCase();
      const mercado = (product?.mercado || '').toLowerCase();
      
      return name.includes(searchLower) || 
             description.includes(searchLower) || 
             metal.includes(searchLower) || 
             mercado.includes(searchLower);
    });
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  return (
    <Box className="products-container">
      {/* Header de la página */}
      <Box className="products-header">
        <Container maxWidth="lg" className="products-header-content">
          <Typography variant="h2" component="h1" className="products-title" gutterBottom>
            Catálogo de Productos
          </Typography>
          <Typography variant="h6" className="products-subtitle">
            Explora nuestra amplia selección de metales y productos industriales
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Filtros y búsqueda */}
        <Paper className="filters-section">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Buscar productos por nombre, descripción, metal o mercado..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'white'
                  } 
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Filtros rápidos:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['Acero', 'Aluminio', 'Cobre', 'Zinc'].map((metal) => (
                  <Chip
                    key={metal}
                    label={metal}
                    className="filter-chip"
                    size="small"
                    onClick={() => setSearchTerm(metal)}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Grid de productos */}
        {loading ? (
          <Box className="loading-container">
            <div className="loading-spinner"></div>
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Box className="no-results">
            <SearchIcon className="no-results-icon" />
            <Typography variant="h6" gutterBottom>
              No se encontraron productos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Intenta ajustar los filtros de búsqueda
            </Typography>
          </Box>
        ) : (
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <div key={product.id} className="product-item">
                {product?.images?.length ? (
                  <img 
                    src={product.images[0].image.startsWith('http') ? 
                      product.images[0].image.replace(/^http:/, 'https:') : 
                      `${API_ROOT_URL}${product.images[0].image}`} 
                    alt={generarNombreProducto(product)}
                    className="product-image"
                  />
                ) : (
                  <Box 
                    className="product-image"
                    sx={{ 
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '2rem'
                    }}
                  >
                    <BusinessIcon />
                  </Box>
                )}
                
                <div className="product-content">
                  <Typography variant="h6" className="product-title">
                    {generarNombreProducto(product)}
                  </Typography>
                  
                  <div className="product-meta">
                    <Chip 
                      label={product?.mercado || 'N/A'} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    <Typography className="product-price">
                      {product?.contact_for_price ? (
                        <span className="product-contact">Cotizar</span>
                      ) : (
                        `$${product?.price || 'N/A'}`
                      )}
                    </Typography>
                  </div>
                  
                  {/* Información del proveedor */}
                  {product?.supplier && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.85rem' }}>
                      <BusinessIcon sx={{ fontSize: '14px', mr: 0.5, verticalAlign: 'middle' }} />
                      {product.supplier.business_profile?.company_name || product.supplier.username}
                    </Typography>
                  )}
                  
                  {product?.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {product.description.length > 100 
                        ? `${product.description.substring(0, 100)}...` 
                        : product.description
                      }
                    </Typography>
                  )}
                  
                  <div className="product-specs">
                    {especificacionesClave(product.especificaciones).slice(0, 3).map((spec, index) => (
                      <Chip 
                        key={index} 
                        label={spec} 
                        size="small" 
                        className="spec-chip"
                      />
                    ))}
                  </div>
                  
                  <Box sx={{ mt: 2 }}>
                    <Button
                      component={Link}
                      to={`/products/${product.id}`}
                      variant="outlined"
                      fullWidth
                      className="custom-button"
                      sx={{ 
                        borderColor: 'var(--primary-color)',
                        color: 'var(--primary-color)',
                        '&:hover': {
                          borderColor: 'var(--primary-dark)',
                          backgroundColor: 'rgba(244, 63, 94, 0.05)'
                        }
                      }}
                    >
                      Ver Detalles
                    </Button>
                  </Box>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </Box>
  );
};

export default Products; 